/**
 * Intake reservation session: draft SR/EoC, heartbeat extend, referral slot reserve/release, promote.
 */

import type { MedplumClient } from '@medplum/core';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { intakeTherapyTypeToServiceTypeCode } from '../../../lib/serviceRequestFhirConstants';
import type { BookingReservation, IntakeAnswers } from './types';
import { getActiveTherapyTypes, getProviderSelectionTherapyTypes, isHighRisk } from './types';
import {
  createIntakeDraftResourcesViaBot,
  extendIntakeReservationViaBot,
  patchIntakeProposedAppointmentViaBot,
  promoteIntakeSessionViaBot,
  releaseReferralSlotViaBot,
  reserveReferralSlotViaBot,
} from './intakeLeadPatient';

const HEARTBEAT_MIN_MS = 60_000;

export interface IntakeDraftPair {
  serviceRequestId: string;
  episodeOfCareId: string;
  therapyServiceTypeCode: string;
}

export interface UseIntakeSessionOptions {
  medplum: MedplumClient;
  patientId: string | null;
  /** Referral-request Task id for capacity reservation (pilot: env). */
  referralTaskId: string | null;
  /** When false, no-op reservation APIs (e.g. mock mode). */
  enabled: boolean;
  /** Practice receiving this intake — scopes draft/promoted ServiceRequests. */
  organizationId?: string;
}

export interface UseIntakeSessionReturn {
  sessionId: string;
  draftReady: boolean;
  draftPairs: IntakeDraftPair[];
  reservationExpiresAtIso: string | null;
  secondsUntilExpiry: number | null;
  showExpiryWarning: boolean;
  expired: boolean;
  proposedAppointmentId: string | null;
  /** Creates draft SR/EoC pairs when needed; returns the pairs (existing or newly created). */
  createDraftResources: (answers: IntakeAnswers) => Promise<IntakeDraftPair[]>;
  reserveForBookingRound: (
    answers: IntakeAnswers,
    bookingRound: number,
    /** The selected provider's referral-request Task id (from matching); defaults to the session option. */
    referralTaskIdOverride?: string,
  ) => Promise<BookingReservation | null>;
  releaseCurrentReservation: (fallback?: BookingReservation) => Promise<void>;
  /** Release a specific (possibly prior-round) booking's referral slot + held calendar Slot. */
  releaseReservationForBooking: (reservation: BookingReservation) => Promise<void>;
  patchProposedAppointmentTime: (
    appointmentId: string,
    bookingDate: string | null,
    bookingTime: string | null,
    durationMinutes: number,
  ) => Promise<void>;
  promoteSession: (answers: IntakeAnswers) => Promise<void>;
}

/**
 * Resolve which referral slot a release should target. The explicit fallback (the exact booking
 * the caller is editing) wins over the hook's live refs, which on cross-round back-nav point at a
 * different/later round; the session-level referralTaskId is the last resort.
 */
export function resolveReleaseTarget(
  fallback: BookingReservation | undefined,
  refs: { episodeOfCareId: string | null; referralTaskId: string | null },
  optionReferralTaskId: string | null,
): { taskId: string | null; eocId: string | null } {
  return {
    taskId: fallback?.referralTaskId ?? refs.referralTaskId ?? optionReferralTaskId,
    eocId: fallback?.episodeOfCareId ?? refs.episodeOfCareId ?? null,
  };
}

function newSessionId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `intake-sess-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function useIntakeSession(options: UseIntakeSessionOptions): UseIntakeSessionReturn {
  const { medplum, patientId, referralTaskId, enabled, organizationId } = options;

  const sessionIdRef = useRef<string>(newSessionId());
  const sessionId = sessionIdRef.current;

  const [reservationExpiresAtIso, setReservationExpiresAtIso] = useState<string | null>(null);
  const [draftPairs, setDraftPairs] = useState<IntakeDraftPair[]>([]);
  const [draftReady, setDraftReady] = useState(false);
  const [expired, setExpired] = useState(false);
  const [nowTick, setNowTick] = useState(() => Date.now());
  const lastHeartbeatAt = useRef(0);
  const [proposedAppointmentId, setProposedAppointmentId] = useState<string | null>(null);
  const currentEpisodeIdRef = useRef<string | null>(null);
  const currentReferralTaskIdRef = useRef<string | null>(null);

  useEffect(() => {
    const t = window.setInterval(() => setNowTick(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, []);

  const expiresAtDate = useMemo(
    () => (reservationExpiresAtIso ? new Date(reservationExpiresAtIso) : null),
    [reservationExpiresAtIso],
  );

  const secondsUntilExpiry = useMemo(() => {
    if (!expiresAtDate || expired) return null;
    return Math.max(0, Math.floor((expiresAtDate.getTime() - nowTick) / 1000));
  }, [expiresAtDate, nowTick, expired]);

  const showExpiryWarning = (secondsUntilExpiry ?? 0) > 0 && (secondsUntilExpiry ?? 0) <= 120;

  useEffect(() => {
    if (secondsUntilExpiry === 0 && expiresAtDate && !expired) {
      setExpired(true);
    }
  }, [secondsUntilExpiry, expiresAtDate, expired]);

  const bumpHeartbeat = useCallback(async () => {
    if (!enabled || !draftReady || expired) return;
    const now = Date.now();
    if (now - lastHeartbeatAt.current < HEARTBEAT_MIN_MS) return;
    lastHeartbeatAt.current = now;
    try {
      const r = await extendIntakeReservationViaBot(medplum, { sessionId });
      setReservationExpiresAtIso(r.reservationExpiresAt);
    } catch {
      // best-effort
    }
  }, [enabled, draftReady, expired, medplum, sessionId]);

  useEffect(() => {
    if (!enabled || expired) return;
    const onActivity = (): void => {
      void bumpHeartbeat();
    };
    window.addEventListener('click', onActivity, true);
    window.addEventListener('keydown', onActivity, true);
    window.addEventListener('scroll', onActivity, true);
    window.addEventListener('touchstart', onActivity, true);
    return () => {
      window.removeEventListener('click', onActivity, true);
      window.removeEventListener('keydown', onActivity, true);
      window.removeEventListener('scroll', onActivity, true);
      window.removeEventListener('touchstart', onActivity, true);
    };
  }, [enabled, expired, bumpHeartbeat]);

  const draftPairsRef = useRef<IntakeDraftPair[]>([]);
  const draftInFlightRef = useRef<Promise<IntakeDraftPair[]> | null>(null);

  const createDraftResources = useCallback(
    async (answers: IntakeAnswers): Promise<IntakeDraftPair[]> => {
      if (!enabled || !patientId) {
        throw new Error('Intake session is not ready to create draft resources.');
      }
      if (draftReady) return draftPairsRef.current;
      if (draftInFlightRef.current) return draftInFlightRef.current;

      const codes = getActiveTherapyTypes(answers).map(intakeTherapyTypeToServiceTypeCode);
      if (codes.length === 0) {
        throw new Error('No therapy services selected for draft intake resources.');
      }

      const inflight = (async (): Promise<IntakeDraftPair[]> => {
        const result = await createIntakeDraftResourcesViaBot(medplum, {
          patientId,
          sessionId,
          organizationId,
          therapyTypeCodes: codes,
          topics: answers.topics,
          approaches: answers.specialties,
          providerPreferences: answers.demographicPrefs,
          availabilityTimeOfDay: answers.availabilityTimeOfDay,
          availabilityDays: answers.availabilityDays,
          sessionFormat: answers.format,
          preferredLocationIds: answers.preferredLocationIds,
          paymentType:
            answers.payType === 'insurance'
              ? 'insurance'
              : answers.payType === 'cash'
                ? 'self-pay'
                : undefined,
          ...(codes.includes('group')
            ? {
                groupSelection: answers.groupSelection,
                requestedGroupId: answers.requestedGroupId,
                requestedGroupTemplateId: answers.requestedGroupTemplateId,
              }
            : {}),
        });

        setReservationExpiresAtIso(result.reservationExpiresAt);
        const pairs: IntakeDraftPair[] = result.pairs.map((p, i) => ({
          serviceRequestId: p.serviceRequest.id as string,
          episodeOfCareId: p.episodeOfCare.id as string,
          therapyServiceTypeCode: codes[i] as string,
        }));
        draftPairsRef.current = pairs;
        setDraftPairs(pairs);
        setDraftReady(true);
        return pairs;
      })();

      draftInFlightRef.current = inflight;
      try {
        return await inflight;
      } finally {
        if (draftInFlightRef.current === inflight) {
          draftInFlightRef.current = null;
        }
      }
    },
    [enabled, patientId, medplum, sessionId, draftReady, organizationId],
  );

  const reserveForBookingRound = useCallback(
    async (
      answers: IntakeAnswers,
      bookingRound: number,
      referralTaskIdOverride?: string,
    ): Promise<BookingReservation | null> => {
      const effectiveReferralTaskId = referralTaskIdOverride ?? referralTaskId;
      if (!enabled || !effectiveReferralTaskId) return null;
      // Index the SAME list the loop uses (excludes screening/skipped) so we reserve the slot for
      // the round's actual service EpisodeOfCare.
      const activeTypes = getProviderSelectionTherapyTypes(answers);
      const therapyType = activeTypes[bookingRound];
      if (!therapyType || !answers.selectedProviderId) return null;

      const code = intakeTherapyTypeToServiceTypeCode(therapyType);
      const pair = draftPairs.find((p) => p.therapyServiceTypeCode === code);
      if (!pair) return null;

      const res = await reserveReferralSlotViaBot(medplum, {
        referralTaskId: effectiveReferralTaskId,
        episodeOfCareId: pair.episodeOfCareId,
        sessionId,
        practitionerId: answers.selectedProviderId,
        assignmentLabel: `${therapyType} hold`,
      });
      setProposedAppointmentId(res.appointmentId);
      currentEpisodeIdRef.current = pair.episodeOfCareId;
      currentReferralTaskIdRef.current = effectiveReferralTaskId;
      return {
        episodeOfCareId: pair.episodeOfCareId,
        serviceRequestId: pair.serviceRequestId,
        referralTaskId: effectiveReferralTaskId,
        appointmentId: res.appointmentId,
      };
    },
    [enabled, referralTaskId, draftPairs, medplum, sessionId],
  );

  const releaseCurrentReservation = useCallback(
    async (fallback?: BookingReservation) => {
      const { taskId, eocId } = resolveReleaseTarget(
        fallback,
        { episodeOfCareId: currentEpisodeIdRef.current, referralTaskId: currentReferralTaskIdRef.current },
        referralTaskId,
      );
      try {
        if (!enabled || !taskId || !eocId) return;
        await releaseReferralSlotViaBot(medplum, {
          referralTaskId: taskId,
          episodeOfCareId: eocId,
          sessionId,
        });
      } finally {
        // Never hold a stale handle locally; the durable reducer reservation is the retry source.
        setProposedAppointmentId(null);
        currentEpisodeIdRef.current = null;
        currentReferralTaskIdRef.current = null;
      }
    },
    [enabled, referralTaskId, medplum, sessionId],
  );

  const releaseReservationForBooking = useCallback(
    async (reservation: BookingReservation) => {
      if (!enabled) return;
      const taskId = reservation.referralTaskId ?? referralTaskId;
      if (!taskId || !reservation.episodeOfCareId) return;
      await releaseReferralSlotViaBot(medplum, {
        referralTaskId: taskId,
        episodeOfCareId: reservation.episodeOfCareId,
        sessionId,
      });
    },
    [enabled, referralTaskId, medplum, sessionId],
  );

  const patchProposedAppointmentTime = useCallback(
    async (
      appointmentId: string,
      bookingDate: string | null,
      bookingTime: string | null,
      durationMinutes: number,
    ) => {
      if (!bookingDate || !bookingTime) return;
      const start = new Date(`${bookingDate}T${bookingTime}:00`);
      if (Number.isNaN(start.getTime())) return;
      const end = new Date(start.getTime() + durationMinutes * 60_000);
      await patchIntakeProposedAppointmentViaBot(medplum, {
        appointmentId,
        sessionId,
        start: start.toISOString(),
        end: end.toISOString(),
      });
    },
    [medplum, sessionId],
  );

  const promoteSession = useCallback(
    async (answers: IntakeAnswers) => {
      if (!enabled || !patientId) return;
      const paymentType = answers.payType === 'insurance' ? 'insurance' : answers.payType === 'cash' ? 'self-pay' : undefined;
      // Services cancelled on the overview are torn down (not promoted) by the bot.
      const cancelledEpisodeIds = answers.bookings
        .filter((b) => b.cancelled && b.reservation?.episodeOfCareId)
        .map((b) => b.reservation!.episodeOfCareId as string);
      const waitlistedTherapyTypeCodes = (answers.waitlistedTherapyTypes ?? []).map(
        intakeTherapyTypeToServiceTypeCode,
      );
      const therapyCodes = getActiveTherapyTypes(answers).map(intakeTherapyTypeToServiceTypeCode);
      await promoteIntakeSessionViaBot(medplum, {
        sessionId,
        patientId,
        organizationId,
        paymentType: paymentType ?? null,
        isHighRisk: isHighRisk(answers),
        cancelledEpisodeIds: cancelledEpisodeIds.length ? cancelledEpisodeIds : undefined,
        waitlistedTherapyTypeCodes: waitlistedTherapyTypeCodes.length
          ? waitlistedTherapyTypeCodes
          : undefined,
        ...(therapyCodes.includes('group')
          ? {
              groupSelection: answers.groupSelection,
              requestedGroupId: answers.requestedGroupId,
              requestedGroupTemplateId: answers.requestedGroupTemplateId,
            }
          : {}),
      });
    },
    [enabled, patientId, medplum, sessionId, organizationId],
  );

  return {
    sessionId,
    draftReady,
    draftPairs,
    reservationExpiresAtIso,
    secondsUntilExpiry,
    showExpiryWarning,
    expired,
    proposedAppointmentId,
    createDraftResources,
    reserveForBookingRound,
    releaseCurrentReservation,
    releaseReservationForBooking,
    patchProposedAppointmentTime,
    promoteSession,
  };
}